import { Check, Edit, Eye, Plus } from 'react-feather';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { GetParticipantDataQuery as GetParticipantData, Permission } from '__generated__/graphql';

import GetParticipantDataQuery from '../GetParticipantDataQuery';
import GetRegistrationSummaryQuery from './GetRegistrationSummaryQuery';
import ProtectedSection from '../../../Common/ProtectedSection';
import RegistrationSummaryList from './RegistrationSummaryList';
import UI from '../../../../common/UI';
import useAddNoteModal from './useAddNoteModal';
import useDeactivateParticipantModal from './useDeactivateParticipantModal';
import useDocumentTitle from '../../../../common/useDocumentTitle';
import useEditParticipantModal from './useEditParticipantModal';
import useLocale from '../../../../common/useLocale';
import usePickDashboardModal from './usePickDashboardModal';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';
import useUrlState from '../../../../common/useUrlState';

type Participant = GetParticipantData['project']['participant'];

export interface AboutPageProps {
  participantId: string;
}

const AboutPage = ({ participantId }: AboutPageProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate } = useLocale();
  const project = useProject();

  const [urlState, setUrlState] = useUrlState({
    parse: (params, { string }) => ({
      event: string(params.event),
    }),
  });

  const { data } = useQuery(
    GetParticipantDataQuery,
    {
      variables: {
        project_id: project.id,
        participant_id: participantId,
      },
    },
  );

  const participant = data?.project.participant;

  useDocumentTitle(participant?.full_name || t('participants'));

  useEffect(() => {
    if (participant && !participant.registration_summaries.find((summary) => summary.event.id === urlState.event)) {
      setUrlState((params) => ({ ...params, event: null }), true);
    }
  }, [participant, urlState.event, setUrlState]);

  const [openDeactivateParticipantModal] = useDeactivateParticipantModal({
    participant,
    refetchQueries: [GetParticipantDataQuery, GetRegistrationSummaryQuery],
  });

  const [openEditParticipantModal] = useEditParticipantModal({
    participant,
  });

  const [openPickDashboardModal] = usePickDashboardModal({
    participant,
  });

  const firstAndOnlyDashboardUrl = participant && participant.registration_summaries.length === 1
    ? participant.registration_summaries[0]?.dashboard_url
    : null;

  if (!participant) {
    return <UI.PageLoader />;
  }

  return (
    <UI.Section>
      <UI.GridContainer sc={{ columns: ['1fr', '1fr 3fr'] }}>
        <UI.Div>
          <UI.GridContainer>
            <UI.FlexContainer sc={{ alignItems: 'center' }}>
              <UI.SectionHeading sc={{ mr: 1 }}>
                {t('about')}
              </UI.SectionHeading>

              <ProtectedSection permissions={[Permission.edit_registrations]}>
                <UI.Button
                  onClick={openEditParticipantModal}
                  sc={{ blank: true, size: 'sm', color: 'gray.600' }}
                  aria-label={t('edit_contact')}
                >
                  <UI.Icon>
                    <Edit />
                  </UI.Icon>
                </UI.Button>
              </ProtectedSection>
            </UI.FlexContainer>

            <UI.GridContainer sc={{ gutter: 0.5 }}>
              <UI.Div>
                <UI.Legend>
                  {t('email_address')}
                </UI.Legend>
                {participant.email}
              </UI.Div>
              <UI.Div>
                <UI.Legend>
                  {t('preferred_language')}
                </UI.Legend>
                <UI.Div>
                  {t(`common:locales.${participant.locale}`)}
                </UI.Div>
              </UI.Div>
              {participant.granted_mail_permissions.length > 0 && (
                <UI.Div>
                  <UI.Legend>
                    {t('mail_permissions')}
                  </UI.Legend>
                  <UI.Div>
                    {participant.granted_mail_permissions.map((mailPermission) => (
                      <UI.Div
                        key={mailPermission.id}
                      >
                        <UI.Icon sc={{ muted: true }}>
                          <Check />
                        </UI.Icon>
                        {' '}
                        <UI.Span>
                          {mailPermission.title}
                        </UI.Span>
                      </UI.Div>
                    ))}
                  </UI.Div>
                </UI.Div>
              )}
            </UI.GridContainer>
            {participant.active && participant.registration_summaries.length > 0 && (
              <UI.Div>
                {firstAndOnlyDashboardUrl && (
                  <UI.AButton
                    href={firstAndOnlyDashboardUrl}
                    sc={{ brand: 'secondary' }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <UI.Icon>
                      <Eye />
                    </UI.Icon>
                    {' '}
                    {t('open_dashboard')}
                  </UI.AButton>
                )}
                {!firstAndOnlyDashboardUrl && (
                  <UI.Button
                    onClick={openPickDashboardModal}
                    sc={{ brand: 'secondary' }}
                  >
                    <UI.Icon>
                      <Eye />
                    </UI.Icon>
                    {' '}
                    {t('open_dashboard')}
                  </UI.Button>
                )}
              </UI.Div>
            )}

            {participant.active && (
              <ProtectedSection permissions={[Permission.edit_registrations]}>
                <UI.Div>
                  <UI.A
                    onClick={openDeactivateParticipantModal}
                    sc={{ color: 'gray.400', small: true }}
                  >
                    {t('delete_contact')}
                  </UI.A>
                </UI.Div>
              </ProtectedSection>
            )}

            {!participant.active && (
              <UI.Div>
                <UI.Span sc={{ color: 'error' }}>
                  {t('participant_deactivated_description', {
                    date: formatDate(
                      parseDate(participant.deactivated_at, { timezone: 'UTC' }),
                      { format: 'display_compact_date' },
                    ),
                  })}
                  {!participant.anonymized && ` ${t('participant_will_be_anonymized')}`}
                </UI.Span>
              </UI.Div>
            )}

            <UI.HR />

            <LatestNote participant={participant} />
          </UI.GridContainer>
        </UI.Div>

        <UI.Div>
          <RegistrationSummaryList
            participant={participant}
          />
        </UI.Div>
      </UI.GridContainer>
    </UI.Section>
  );
};

interface LatestNoteProps {
  participant: Participant;
}

const LatestNote = ({ participant }: LatestNoteProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate } = useLocale();
  const project = useProject();

  const [openAddNoteModal] = useAddNoteModal(participant.id);

  const latestNote = participant.notes[0];
  const notesCount = participant.notes.length;

  return (
    <UI.GridContainer>
      <UI.FlexContainer sc={{ alignItems: 'center' }}>
        <UI.SectionHeading sc={{ mr: 1 }}>
          {t('latest_note')}
        </UI.SectionHeading>

        <ProtectedSection permissions={[Permission.edit_registrations]}>
          <UI.Button
            onClick={openAddNoteModal}
            sc={{ blank: true, size: 'sm', color: 'gray.600' }}
            aria-label={t('add_note')}
          >
            <UI.Icon>
              <Plus />
            </UI.Icon>
          </UI.Button>
        </ProtectedSection>
      </UI.FlexContainer>
      {latestNote && (
        <UI.Card sc={{ outline: 'gray.250', padding: 3 }}>
          <UI.GridContainer
            sc={{ columns: '30px 1fr', gutter: 0.5 }}
          >
            <UI.Div sc={{ pt: 0.5 }}>
              <UI.ProfilePic sc={{ size: 30 }}>
                <UI.Span>
                  <UI.Span>
                    {(latestNote.user.full_name || latestNote.user.email).charAt(0).toUpperCase()}
                  </UI.Span>
                </UI.Span>
              </UI.ProfilePic>
            </UI.Div>
            <UI.Div>
              <UI.Div sc={{ small: true, strong: true }}>
                {(latestNote.user.full_name || latestNote.user.email)}
              </UI.Div>
              <UI.Div sc={{ muted: true, small: true }}>
                {formatDate(
                  parseDate(latestNote.created_at, { timezone: 'UTC' }),
                  { format: 'display_date_time' },
                )}
              </UI.Div>
            </UI.Div>
          </UI.GridContainer>
          <UI.Div sc={{ mt: 2 }}>
            <UI.NL2BR
              content={latestNote.content}
              maxLines={1}
            />
          </UI.Div>
        </UI.Card>
      )}
      {notesCount > 0 && (
        <UI.Div>
          <UI.Link
            to={`/${project.id}/participants/${participant.id}/notes`}
          >
            {t('view_notes', { count: notesCount })}
          </UI.Link>
        </UI.Div>
      )}
      {!latestNote && (
        <UI.Div sc={{ muted: true }}>
          {t('no_notes_description')}
        </UI.Div>
      )}
    </UI.GridContainer>
  );
};

export default AboutPage;
